import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Custom Select component optimized for mobile browsers
 * Prevents hydration errors by building options using DOM APIs instead of React rendering
 * This avoids the React virtual DOM wrapping issue that causes <span> injection
 * Styled to match Shadcn UI Select component for consistency across the application
 */
export const Select = React.forwardRef(({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Select an option...",
  className = "",
  required = false,
  ...props 
}, forwardedRef) => {
  const localRef = useRef(null);
  const selectRef = forwardedRef || localRef;

  // Build select options using native DOM APIs to avoid React wrapping
  useEffect(() => {
    if (!selectRef.current) return;

    const selectElement = selectRef.current;
    
    // Clear existing options
    selectElement.innerHTML = '';
    
    // Add placeholder
    if (placeholder) {
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.className = 'text-muted-foreground';
      selectElement.appendChild(placeholderOption);
    }
    
    // Add all options
    options.forEach((option) => {
      const optionValue = typeof option === 'string' ? option : option.value;
      const optionLabel = typeof option === 'string' ? option : option.label;
      
      const optionElement = document.createElement('option');
      optionElement.value = optionValue;
      optionElement.textContent = optionLabel;
      selectElement.appendChild(optionElement);
    });
    
    // Set current value
    selectElement.value = value;
  }, [options, placeholder, value, selectRef]);

  return (
    <select
      ref={selectRef}
      value={value}
      onChange={onChange}
      className={cn(
        // Base Shadcn Select styling
        "flex h-12 w-full items-center justify-between rounded-lg border-2 border-stone-200 bg-stone-50",
        "px-4 py-2 text-base shadow-sm transition-colors",
        // Focus states matching Shadcn
        "focus:outline-none focus:ring-1 focus:ring-[#1A4D2E] focus:border-[#1A4D2E]",
        // Disabled state
        "disabled:cursor-not-allowed disabled:opacity-50",
        // Text color
        "text-stone-900",
        // Placeholder styling
        "data-[placeholder]:text-stone-500",
        // Mobile optimization
        "appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%23666%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-no-repeat",
        "bg-[length:20px] bg-[position:right_12px_center]",
        className
      )}
      required={required}
      {...props}
    />
  );
});

Select.displayName = 'Select';

export default Select;
